import { useCallback } from "react";

import { useModalDialog } from "@skilitics/designer-ui-components";

import type { FeatureName } from "./messaging/PremiumFeatures.tsx";
import { UpgradePrompt } from "./messaging/UpgradePrompt.tsx";

/**
 * Return a function that will show a prompt to upgrade for the given feature.
 *
 * @example
 * ```tsx
 * const hasAccess = useHasFeatureAccess("publish");
 * const showPublishUpgradePrompt = useUpgradePrompt("publish");
 *
 * const handlePublishClick = () => {
 *   if (!hasAccess) {
 *     showPublishUpgradePrompt();
 *     return;
 *   }
 *   // Proceed with publish action
 * };
 * ```
 */
export const useUpgradePrompt = (feature: FeatureName): ((dialogId?: string) => void) => {
  const { openModalDialog } = useModalDialog();
  return useCallback(
    (dialogId: string = "upgradePrompt") => {
      openModalDialog(
        {
          children: <UpgradePrompt feature={feature} />,
          slotProps: { paper: { sx: { borderRadius: "12px" } } },
        },
        dialogId,
      );
    },
    [openModalDialog, feature],
  );
};
